import React, { useEffect, useMemo, useRef, useState } from 'react';

/**
 * DateField — compact date picker for toolbars (Open Slots).
 *
 *   value      ISO date ('2026-09-18') or null for "any date"
 *   onChange   (iso | null) => void
 *   options    [{ iso, label }]  — only the dates that actually have something (the
 *              caller builds the labels, e.g. "Today · Fri 18 Sep")
 *   placeholder  shown when nothing is picked (default "Any date")
 *   className    extra classes for the input — pass "sv-search" so it matches the
 *                search box beside it exactly
 *
 * Type to filter ("18", "fri", "sep"), ↑/↓ + Enter to pick, Esc to close,
 * × to clear back to any date. Emits the ISO date, never the label.
 */
const DateField = ({ value, onChange, options = [], placeholder = 'Any date', className = '' }) => {
  const picked = useMemo(() => options.find((o) => o.iso === value) || null, [options, value]);
  const [text, setText] = useState(picked ? picked.label : '');
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const wrap = useRef(null);

  useEffect(() => { setText(picked ? picked.label : ''); }, [picked]);

  useEffect(() => {
    const away = (e) => {
      if (wrap.current && !wrap.current.contains(e.target)) { setOpen(false); setText(picked ? picked.label : ''); }
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [picked]);

  const rows = useMemo(() => {
    const q = text.trim().toLowerCase();
    if (!q || (picked && q === picked.label.toLowerCase())) return options;   // opened on a picked date → show all
    return options.filter((o) => o.label.toLowerCase().includes(q) || o.iso.includes(q));
  }, [options, text, picked]);

  const choose = (o) => { onChange(o ? o.iso : null); setText(o ? o.label : ''); setOpen(false); };

  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHi((h) => Math.min(h + 1, rows.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (rows[hi]) choose(rows[hi]); }
    else if (e.key === 'Escape') { setOpen(false); setText(picked ? picked.label : ''); }
    else if (e.key === 'Backspace' && !text && picked) { choose(null); }
  };

  return (
    <div className={`df-wrap ${picked ? 'df-picked' : ''}`} ref={wrap}>
      <svg className="df-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
      </svg>
      <input
        className={`df-input ${className}`}
        value={text}
        placeholder={placeholder}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-label="Filter by date"
        onChange={(e) => { setText(e.target.value); setHi(0); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
      />
      {picked && (
        <button type="button" className="df-clear" aria-label="Clear date" onMouseDown={(e) => { e.preventDefault(); choose(null); }}>×</button>
      )}

      {open && (
        <ul className="df-list" role="listbox">
          <li role="option" aria-selected={!picked} className={`df-opt df-any ${!picked ? 'df-sel' : ''}`} onMouseDown={(e) => { e.preventDefault(); choose(null); }}>
            {placeholder}
          </li>
          {rows.length === 0 && <li className="df-opt df-none">No dates match</li>}
          {rows.map((o, i) => (
            <li key={o.iso} role="option" aria-selected={o.iso === value}
                className={`df-opt ${i === hi ? 'df-hi' : ''} ${o.iso === value ? 'df-sel' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); choose(o); }}
                onMouseEnter={() => setHi(i)}>
              {o.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default DateField;